<!--
  DAVMAN AI INTEGRATION
  Este archivo es parte de la integración personalizada de IA para Davman Persa.
  NO es parte del código original de Chatwoot.

  Pestaña de gestión de FAQs del asistente IA
-->
<script setup>
import { ref, onMounted, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  getFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
} from 'dashboard/api/davmanAi';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['updated']);

const loading = ref(true);
const saving = ref(false);
const faqs = ref([]);
const showModal = ref(false);
const editingFaq = ref(null);
const deleteConfirmFaq = ref(null);

// Form data
const faqForm = ref({
  title: '',
  keywords: '',
  response: '',
  auto_reply: false,
  category: 'general',
});

const categories = [
  { value: 'general', label: 'General' },
  { value: 'horarios', label: 'Horarios' },
  { value: 'productos', label: 'Productos' },
  { value: 'pagos', label: 'Pagos' },
  { value: 'envios', label: 'Envíos' },
  { value: 'garantias', label: 'Garantías' },
  { value: 'citas', label: 'Citas' },
];

const isEditing = computed(() => !!editingFaq.value);

// Computed for delete modal visibility (v-model requires a member expression, not !!expression)
const showDeleteModal = computed({
  get: () => !!deleteConfirmFaq.value,
  set: (val) => {
    if (!val) deleteConfirmFaq.value = null;
  },
});

const loadFaqs = async () => {
  loading.value = true;
  try {
    const data = await getFaqs();
    faqs.value = data.faqs || [];
  } catch (error) {
    useAlert('Error al cargar las FAQs');
    console.error('[Davman AI] Error loading FAQs:', error);
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  editingFaq.value = null;
  faqForm.value = {
    title: '',
    keywords: '',
    response: '',
    auto_reply: false,
    category: 'general',
  };
  showModal.value = true;
};

const openEditModal = (faq) => {
  editingFaq.value = faq;
  faqForm.value = {
    title: faq.title,
    keywords: (faq.keywords || []).join(', '),
    response: faq.response,
    auto_reply: faq.auto_reply || false,
    category: faq.category || 'general',
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingFaq.value = null;
};

const saveFaq = async () => {
  if (!faqForm.value.title || !faqForm.value.response) {
    useAlert('El título y la respuesta son obligatorios');
    return;
  }

  saving.value = true;
  try {
    const data = {
      title: faqForm.value.title,
      keywords: faqForm.value.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k),
      response: faqForm.value.response,
      auto_reply: faqForm.value.auto_reply,
      category: faqForm.value.category,
    };

    if (isEditing.value) {
      await updateFaq(editingFaq.value.id, data);
      useAlert('FAQ actualizada correctamente');
    } else {
      await createFaq(data);
      useAlert('FAQ creada correctamente');
    }

    closeModal();
    await loadFaqs();
    emit('updated');
  } catch (error) {
    useAlert('Error al guardar la FAQ');
    console.error('[Davman AI] Error saving FAQ:', error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (faq) => {
  deleteConfirmFaq.value = faq;
};

const cancelDelete = () => {
  deleteConfirmFaq.value = null;
};

const handleDelete = async () => {
  if (!deleteConfirmFaq.value) return;

  try {
    await deleteFaq(deleteConfirmFaq.value.id);
    useAlert('FAQ eliminada correctamente');
    deleteConfirmFaq.value = null;
    await loadFaqs();
    emit('updated');
  } catch (error) {
    useAlert('Error al eliminar la FAQ');
    console.error('[Davman AI] Error deleting FAQ:', error);
  }
};

const getCategoryLabel = (value) => {
  const cat = categories.find(c => c.value === value);
  return cat ? cat.label : value;
};

onMounted(() => {
  loadFaqs();
});
</script>

<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-medium text-n-slate-12">
          Preguntas Frecuentes
        </h3>
        <p class="text-sm text-n-slate-11">
          Configura respuestas predefinidas para preguntas comunes.
        </p>
      </div>
      <Button
        icon="i-lucide-plus"
        label="Nueva FAQ"
        @click="openCreateModal"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="faqs.length === 0"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <span class="i-lucide-message-circle-question size-12 text-n-slate-9 mb-4" />
      <h4 class="text-lg font-medium text-n-slate-12">
        No hay FAQs configuradas
      </h4>
      <p class="text-sm text-n-slate-11 mb-4">
        Crea tu primera pregunta frecuente para empezar.
      </p>
      <Button
        icon="i-lucide-plus"
        label="Crear primera FAQ"
        @click="openCreateModal"
      />
    </div>

    <!-- FAQ List -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="faq in faqs"
        :key="faq.id"
        class="p-4 rounded-xl border border-n-weak bg-n-solid-2 hover:border-n-strong transition-colors"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-2">
              <h4 class="font-medium text-n-slate-12">{{ faq.title }}</h4>
              <span
                class="px-2 py-0.5 text-xs rounded-full bg-n-solid-3 text-n-slate-11"
              >
                {{ getCategoryLabel(faq.category) }}
              </span>
              <span
                v-if="faq.auto_reply"
                class="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              >
                Auto-respuesta
              </span>
            </div>

            <p class="text-sm text-n-slate-11 line-clamp-2 mb-2">
              {{ faq.response }}
            </p>

            <div v-if="faq.keywords?.length" class="flex flex-wrap gap-1">
              <span
                v-for="keyword in faq.keywords.slice(0, 5)"
                :key="keyword"
                class="px-2 py-0.5 text-xs rounded-full bg-n-solid-3 text-n-slate-10"
              >
                {{ keyword }}
              </span>
              <span
                v-if="faq.keywords.length > 5"
                class="px-2 py-0.5 text-xs text-n-slate-10"
              >
                +{{ faq.keywords.length - 5 }} más
              </span>
            </div>
          </div>

          <div class="flex gap-1 flex-shrink-0">
            <Button
              v-tooltip.top="'Editar'"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="openEditModal(faq)"
            />
            <Button
              v-tooltip.top="'Eliminar'"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="confirmDelete(faq)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <woot-modal v-model:show="showModal" :on-close="closeModal">
      <div class="p-6 w-full max-w-2xl">
        <h3 class="text-lg font-medium text-n-slate-12 mb-6">
          {{ isEditing ? 'Editar FAQ' : 'Nueva FAQ' }}
        </h3>

        <div class="flex flex-col gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              Título *
            </label>
            <Input
              v-model="faqForm.title"
              placeholder="Ej: Horarios de atención"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              Keywords
            </label>
            <Input
              v-model="faqForm.keywords"
              placeholder="horario, hora, abierto, atención (separados por coma)"
            />
            <p class="text-xs text-n-slate-11 mt-1">
              Palabras clave que activan esta respuesta, separadas por comas.
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              Respuesta *
            </label>
            <textarea
              v-model="faqForm.response"
              placeholder="La respuesta que el asistente dará..."
              rows="4"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 resize-y focus:border-n-strong focus:outline-none"
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1">
                Categoría
              </label>
              <select
                v-model="faqForm.category"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
              >
                <option
                  v-for="cat in categories"
                  :key="cat.value"
                  :value="cat.value"
                >
                  {{ cat.label }}
                </option>
              </select>
            </div>

            <div class="flex items-center">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="faqForm.auto_reply"
                  type="checkbox"
                  class="size-4 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-12">
                  Auto-respuesta activa
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-n-weak">
          <Button
            label="Cancelar"
            color="slate"
            faded
            @click="closeModal"
          />
          <Button
            :label="isEditing ? 'Guardar cambios' : 'Crear FAQ'"
            icon="i-lucide-save"
            :is-loading="saving"
            @click="saveFaq"
          />
        </div>
      </div>
    </woot-modal>

    <!-- Delete Confirmation Modal -->
    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="cancelDelete"
      :on-confirm="handleDelete"
      title="Eliminar FAQ"
      message="¿Estás seguro de que deseas eliminar esta FAQ?"
      :message-value="deleteConfirmFaq?.title"
      confirm-text="Sí, eliminar"
      reject-text="No, cancelar"
    />
  </div>
</template>
